import { ApolloClient, InMemoryCache } from '@apollo/client';
import { SetContextLink } from '@apollo/client/link/context';
import { ErrorLink } from '@apollo/client/link/error';
import { HttpLink } from '@apollo/client/link/http';

import { accessToken, clear as clearSession } from '@/auth/session';
import { keystoneGraphqlEndpoint } from '@/config/runtime-config';

/**
 * The Keystone GraphQL client.
 *
 * Keystone accepts Turnstile's identity token directly (its `turnstile-jwt`
 * token type), so the portal talks to Keystone with the same token it logged
 * in with — there is no app-api hop, and no delegation token to mint.
 *
 * `credentials` stays omitted: Keystone's CORS is `allow_credentials=False`,
 * so the only thing that authenticates a call is this Authorization header.
 */
export function createApolloClient(): ApolloClient {
  const auth = new SetContextLink(async (prevContext) => {
    const token = await accessToken();
    return {
      headers: {
        ...prevContext.headers,
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    };
  });

  // A 401 here means the session is unrecoverable — the token was refreshed
  // moments ago in the auth link above, so retrying would only spend another
  // round trip on the same answer. Dropping the identity re-renders the app at
  // the sign-in screen.
  const onUnauthenticated = new ErrorLink(({ error }) => {
    if (error && 'statusCode' in error && error.statusCode === 401) {
      clearSession();
    }
  });

  return new ApolloClient({
    link: auth.concat(onUnauthenticated).concat(new HttpLink({ uri: keystoneGraphqlEndpoint() })),
    cache: new InMemoryCache(),
    devtools: { enabled: true },
  });
}
